import { createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../../../services/apiService";
import { FEATURE_FLOW_SLICE_NAME } from "../../sliceConstants";
import { checkIsAdmin } from "../../../../helper/utils";
import { initHoFiltersFromAccess } from "../../../../services/hoFilter";

// Normalize a (value, text) pair into a [{ value, name }] list.
// Legacy scalar shape: business: "BU000038", business_text: "Electronics ...".
const toEntries = (values, texts) => {
  const v = Array.isArray(values) ? values : values != null && values !== "" ? [values] : [];
  const t = Array.isArray(texts) ? texts : texts != null && texts !== "" ? [texts] : [];
  return v.map((value, i) => ({ value, name: t[i] ?? t[0] ?? value }));
};

// Normalize the new array shape into [{ value, name }]:
//   businesses: [{ id: "BU000038", name: "Electronics Digital Stores" }, ...]
// Tolerates id|value and name|text|business_text keys.
const fromList = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => {
      const value = item?.id ?? item?.value ?? item?.business;
      const name = item?.name ?? item?.text ?? item?.business_text ?? value;
      return value ? { value, name } : null;
    })
    .filter(Boolean);

// addonBusiness groups extra businesses under their category:
//   [{ category_id, category_text, businesses: [{ business_id, business_text }] }]
// Flatten into the business [{ value, name }] list.
const businessesFromAddon = (addon) =>
  (Array.isArray(addon) ? addon : [])
    .flatMap((cat) => (Array.isArray(cat?.businesses) ? cat.businesses : []))
    .map((b) => (b?.business_id ? { value: b.business_id, name: b.business_text ?? b.business_id } : null))
    .filter(Boolean);

// The categories carried alongside the addon businesses -> [{ value, name }].
const categoriesFromAddon = (addon) =>
  (Array.isArray(addon) ? addon : [])
    .map((cat) => (cat?.category_id ? { value: cat.category_id, name: cat.category_text ?? cat.category_id } : null))
    .filter(Boolean);

// Which category each business sits under -> [{ business, category }].
// Primary: one category covers every primary business (or pair by index when
// the lists line up); addon: each group's category covers its businesses.
const businessCategoryPairs = (primaryBusinesses, primaryCategories, addon) => {
  const pairable =
    primaryCategories.length === 1 || primaryCategories.length === primaryBusinesses.length;
  const primary = pairable
    ? primaryBusinesses.map((b, i) => ({
        business: b.value,
        category: (primaryCategories[i] || primaryCategories[0]).value,
      }))
    : [];
  const fromAddon = (Array.isArray(addon) ? addon : []).flatMap((cat) =>
    (Array.isArray(cat?.businesses) ? cat.businesses : [])
      .filter((b) => b?.business_id && cat?.category_id)
      .map((b) => ({ business: b.business_id, category: cat.category_id }))
  );
  const seen = new Set();
  return [...primary, ...fromAddon].filter((p) => !seen.has(p.business) && seen.add(p.business));
};

// Drop entries with no value or a value already seen (first wins).
const dedupeByValue = (entries) => {
  const seen = new Set();
  return entries.filter(({ value }) => {
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const featureThunkActions = {
  fetchAllowedIds: createAsyncThunk(
    `${FEATURE_FLOW_SLICE_NAME}/fetchAllowedIds`,
    async ({ empId, email }, { rejectWithValue, getState }) => {
      try {
        const apiResponse = await apiService.getAllowedFeatureIds({ empId, email });
        const parsed =
          typeof apiResponse?.data === "string"
            ? JSON.parse(apiResponse.data)
            : apiResponse?.data;
        // access-management shape:
        //   { data: { resources: [{ resource_id }],
        //             business, business_text, category, category_text,
        //             addonBusiness: [{ category_id, category_text,
        //                               businesses: [{ business_id, business_text }] }] } }
        // Also tolerates the array shape (businesses/categories: [{ id, name }])
        // and the flat { resources: [...] } / { ids: [...] }.
        const payload = parsed?.data ?? parsed ?? {};
        const ids = Array.isArray(payload?.resources)
          ? payload.resources.map((r) => r?.resource_id).filter(Boolean)
          : payload?.ids || [];
        // The primary business/category (array shape first, then legacy scalar),
        // merged with the addonBusiness entries; dedupe so the primary business
        // isn't repeated when it also appears in an addon group.
        const primaryBusinesses = payload?.businesses
          ? fromList(payload.businesses)
          : toEntries(payload?.business, payload?.business_text);
        const primaryCategories = payload?.categories
          ? fromList(payload.categories)
          : toEntries(payload?.category, payload?.category_text);
        const businesses = dedupeByValue([
          ...primaryBusinesses,
          ...businessesFromAddon(payload?.addonBusiness),
        ]);
        const categories = dedupeByValue([
          ...primaryCategories,
          ...categoriesFromAddon(payload?.addonBusiness),
        ]);
        // Persist the HO filter here rather than in Header's effect: that ran
        // after pages had already fired their first APIs, so the filter only
        // showed up after a reload.
        if (checkIsAdmin(getState().dashboardFlow?.homeDashboardListing?.data)) {
          initHoFiltersFromAccess({ businesses, categories });
        }
        const businessCategories = businessCategoryPairs(
          primaryBusinesses,
          primaryCategories,
          payload?.addonBusiness
        );
        return { ids, businesses, categories, businessCategories };
      } catch (err) {
        return rejectWithValue("Failed to load feature permissions");
      }
    }
  ),
};

export default featureThunkActions;
